/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { AnalyticsTracker } from '@/components/analytics/AnalyticsTracker';
import { AppShell } from '@/components/layout/app-shell';
import { AuthLayout } from '@/components/layout/auth-layout';
import { PublicLayout } from '@/components/layout/public-layout';
import { PageLoading } from '@/components/feedback/page-loading';
import { FeaturePage } from '@/routes/pages/feature-page';
import { LandingPage } from '@/routes/pages/landing-page';
import { LegalPage } from '@/routes/pages/legal-page';
import { LoginPage } from '@/routes/pages/login-page';
import { NotFoundPage } from '@/routes/pages/not-found-page';
import { PricingPage } from '@/routes/pages/pricing-page';
import { RegisterPage } from '@/routes/pages/register-page';
import { AdminLoginPage } from '@/routes/pages/admin-login-page';
import { PasswordSetupPage } from '@/routes/pages/password-setup-page';
import { IntakeLanguageEntry } from '@/features/intake/IntakeLanguageEntry';
import { AdminRoute, ProtectedRoute } from '@/routes/protected-route';
import { RouteErrorPage } from '@/routes/route-error';
import { PurchasedFeatureRoute } from '@/routes/purchased-feature-route';
import { PurchaserClaimPage } from '@/routes/pages/purchaser-claim-page';

const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const OnboardingPage = lazy(() => import('@/features/onboarding/OnboardingPage'));
const CareerProfilePage = lazy(() => import('@/features/career-profile/CareerProfilePage'));
const CareerIntakePage = lazy(() => import('@/features/intake/CareerIntakePage'));
const CvBuilderPage = lazy(() => import('@/features/cv/CvBuilderPage'));
const CvCheckerPage = lazy(() => import('@/features/cv/CvCheckerPage'));
const LinkedInCheckerPage = lazy(() => import('@/features/linkedin/LinkedInCheckerPage'));
const JobMatchPage = lazy(() => import('@/features/jobs/JobMatchPage'));
const InterviewPrepPage = lazy(() => import('@/features/interview/InterviewPrepPage'));
const AccountPage = lazy(() => import('@/features/account/AccountPage'));
const AdminPage = lazy(() => import('@/features/admin/AdminPage'));
const PublicIntakePage = lazy(() => import('@/features/intake/PublicIntakePage'));

function LazyRoute({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoading shell />}>{children}</Suspense>;
}

function AnalyticsRoot() {
  return (
    <>
      <AnalyticsTracker />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <AnalyticsRoot />,
    children: [
      { path: 'form/en', element: <IntakeLanguageEntry language="en" /> },
      { path: 'form/ar', element: <IntakeLanguageEntry language="ar" /> },
      {
        path: 'form/:id',
        errorElement: <RouteErrorPage />,
        element: (
          <LazyRoute>
            <PublicIntakePage />
          </LazyRoute>
        ),
      },
      {
        element: <PublicLayout />,
        errorElement: <RouteErrorPage />,
        children: [
          { index: true, element: <LandingPage /> },
          { path: 'pricing', element: <PricingPage /> },
          { path: 'features/cv-builder', element: <FeaturePage kind="cv" /> },
          { path: 'features/linkedin-checker', element: <FeaturePage kind="linkedin" /> },
          { path: 'features/interview-prep', element: <FeaturePage kind="interview" /> },
          { path: 'privacy', element: <LegalPage type="privacy" /> },
          { path: 'terms', element: <LegalPage type="terms" /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      {
        element: <AuthLayout />,
        errorElement: <RouteErrorPage />,
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: 'register', element: <RegisterPage /> },
          { path: 'admin/login', element: <AdminLoginPage /> },
          { path: 'claim/:token', element: <PurchaserClaimPage /> },
        ],
      },
      {
        path: 'set-password',
        errorElement: <RouteErrorPage />,
        element: (
          <ProtectedRoute>
            <AuthLayout />
          </ProtectedRoute>
        ),
        children: [{ index: true, element: <PasswordSetupPage /> }],
      },
      {
        path: 'onboarding',
        errorElement: <RouteErrorPage />,
        element: (
          <ProtectedRoute completedOnboardingRedirect>
            <LazyRoute>
              <OnboardingPage />
            </LazyRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: 'app',
        errorElement: <RouteErrorPage />,
        element: (
          <ProtectedRoute requireOnboarding>
            <AppShell />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <LazyRoute>
                <DashboardPage />
              </LazyRoute>
            ),
          },
          {
            path: 'dashboard',
            element: (
              <LazyRoute>
                <DashboardPage />
              </LazyRoute>
            ),
          },
          {
            path: 'intake',
            element: (
              <LazyRoute>
                <CareerIntakePage />
              </LazyRoute>
            ),
          },
          {
            path: 'profile',
            element: (
              <LazyRoute>
                <CareerProfilePage />
              </LazyRoute>
            ),
          },
          {
            path: 'cv',
            element: (
              <LazyRoute>
                <CvBuilderPage />
              </LazyRoute>
            ),
          },
          {
            path: 'cv-check',
            element: (
              <PurchasedFeatureRoute feature="deterministic_cv_score">
                <LazyRoute>
                  <CvCheckerPage />
                </LazyRoute>
              </PurchasedFeatureRoute>
            ),
          },
          {
            path: 'linkedin',
            element: (
              <PurchasedFeatureRoute feature="deterministic_linkedin_score">
                <LazyRoute>
                  <LinkedInCheckerPage />
                </LazyRoute>
              </PurchasedFeatureRoute>
            ),
          },
          {
            path: 'jobs',
            element: (
              <PurchasedFeatureRoute feature="basic_job_match">
                <LazyRoute>
                  <JobMatchPage />
                </LazyRoute>
              </PurchasedFeatureRoute>
            ),
          },
          {
            path: 'interview',
            element: (
              <PurchasedFeatureRoute feature="sample_interview_questions">
                <LazyRoute>
                  <InterviewPrepPage />
                </LazyRoute>
              </PurchasedFeatureRoute>
            ),
          },
          {
            path: 'account',
            element: (
              <LazyRoute>
                <AccountPage />
              </LazyRoute>
            ),
          },
          {
            path: 'admin',
            element: (
              <AdminRoute>
                <LazyRoute>
                  <AdminPage />
                </LazyRoute>
              </AdminRoute>
            ),
          },
        ],
      },
    ],
  },
]);
